/**
 * Main Entry Point - Frontend
 * 
 * React application entry point with providers and routing
 */

import React from 'react'
import ReactDOM from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import * as Sentry from '@sentry/react'
import App from './App.jsx'
import './index.css'
import { initTheme } from './lib/theme'
import { scrubEvent, scrubBreadcrumb } from './lib/sentryScrub'

// Before the first render, so the login screen is themed too.
initTheme()

// Initialize Sentry for production only
if (import.meta.env.PROD) {
  Sentry.init({
    dsn: "https://c1718483c718a5606a51be102f7df07c@o1412074.ingest.us.sentry.io/4510473139781632",
    environment: 'production',

    integrations: [
      Sentry.browserTracingIntegration(),
      // Replays show patient records on screen: mask every text node and
      // input, and block images and video (Sentry's defaults, stated here so
      // nobody loosens them by accident).
      Sentry.replayIntegration({ maskAllText: true, maskAllInputs: true, blockAllMedia: true }),
      // Every console.error becomes an event
      Sentry.captureConsoleIntegration({ levels: ['error'] }),
    ],

    // Sentry bills performance per span and a page load is dozens of them
    // (every asset and API call), so 1% of page loads and navigations, and
    // never the TV display, which runs all day on office screens. Errors are
    // unaffected: they are always sent.
    tracesSampler: ({ name }) => {
      const path = String(name || window.location.pathname)
      return path === '/tv' || path.startsWith('/tv/') ? 0 : 0.01
    },

    // Distributed tracing for API calls
    tracePropagationTargets: [
      "localhost",
      /^https:\/\/.*\.railway\.app/,
      /^https:\/\/admin\.5axis\.health/,
    ],

    // Session Replay - 10% of sessions, 100% on error
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,

    // No IPs or cookies, and every event is scrubbed of emails, phones and
    // tokens before it leaves the browser (lib/sentryScrub.js).
    sendDefaultPii: false,
    beforeSend: scrubEvent,
    beforeSendTransaction: scrubEvent,
    beforeBreadcrumb: scrubBreadcrumb,
  })

  console.log('✅ Sentry initialized for frontend')
}

// Create a client for React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error) => {
        // Don't retry on 401/403 errors (authentication issues)
        if (error?.response?.status === 401 || error?.response?.status === 403) {
          return false;
        }
        // Retry up to 3 times for other errors
        return failureCount < 3;
      },
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 10 * 60 * 1000, // 10 minutes
    },
    mutations: {
      retry: false, // Don't retry mutations by default
    },
  },
})

ReactDOM.createRoot(document.getElementById('root')).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Sentry.ErrorBoundary fallback={<p style={{ padding: 24 }}>Something went wrong. Reload the page to try again.</p>}>
          <App />
        </Sentry.ErrorBoundary>
      </BrowserRouter>
    </QueryClientProvider>
  </React.StrictMode>,
)
